/**
 * The real transport. It is written and correct today even though nothing answers it yet, so that
 * turning the mock off is a flag and not a project.
 *
 * `credentials: 'omit'` IS THE POINT OF THIS FILE. The cabinet is bearer-only and carries no
 * cookie on this path (adr/0002-edge-addressing.md); the browser default for a cross-origin
 * request is `same-origin`, which happens to behave correctly, but stating it means a later change
 * to `include` has to be deliberate rather than a copied line. A cookie sent here would also drag
 * the request into a credentialed CORS mode that the API's single allowlist entry does not answer.
 *
 * NO RETRY LIVES HERE. Whether to retry is a decision about what the answer MEANS
 * (adr/0010-firebase-integration.md's 401/503 split), and that decision is `./failure.ts`. A
 * transport that retried on its own would retry a 401 too.
 */
import { AUTHORS_API_BASE_URL } from '../../../config';
import { NetworkUnreachable } from './http';
import type { HttpRequest, HttpResponse, Transport } from './http';

function lowerCasedHeaders(headers: Headers): Readonly<Record<string, string>> {
  const out: Record<string, string> = {};
  headers.forEach((value, name) => {
    out[name.toLowerCase()] = value;
  });
  return out;
}

/**
 * Decodes a body without ever throwing.
 *
 * An edge can answer with HTML — the audited legacy defect was a long article failing to save and
 * being answered with an HTML error page the client could not parse. `null` here means "there was
 * no JSON", which `classifyFailure` already treats as a problem document that is absent rather
 * than malformed.
 */
async function decodeBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  const looksLikeJson = contentType.includes('json');
  if (!looksLikeJson) {
    return null;
  }
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

export const fetchTransport: Transport = async (
  request: HttpRequest,
  signal?: AbortSignal,
): Promise<HttpResponse> => {
  const headers: Record<string, string> = { accept: 'application/json, application/problem+json' };

  if (request.bearerToken !== undefined) {
    headers.authorization = `Bearer ${request.bearerToken}`;
  }
  if (request.body !== undefined) {
    headers['content-type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await fetch(`${AUTHORS_API_BASE_URL}${request.path}`, {
      method: request.method,
      headers,
      // Bearer only. Never a cookie on this surface.
      credentials: 'omit',
      mode: 'cors',
      // `no-store` matters on `GET /v1/me`, which the spec marks `Cache-Control: no-store`: a
      // cached session read would show a disabled author their old capabilities.
      cache: 'no-store',
      ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) }),
      ...(signal === undefined ? {} : { signal }),
    });
  } catch (cause) {
    // `fetch` rejects only for a request that never produced an answer — offline, DNS, TLS, a
    // refused CORS pre-flight. Every HTTP status, including 503, resolves.
    throw new NetworkUnreachable(cause);
  }

  return {
    status: response.status,
    headers: lowerCasedHeaders(response.headers),
    body: await decodeBody(response),
  };
};
